/**
 * Rules Repository — Supabase data layer for rule overrides and audit log.
 */
import { supabase } from './supabase';
import { supabaseUrl } from './supabase';
import { RULES_BY_ID, type RuleOverrides, type RuleValueType } from '../data/ruleRegistry';

// ─── Types ──────────────────────────────────────────────────

export interface RuleOverrideRow {
    id: string;
    value: unknown;
    updated_by: string | null;
    updated_at: string;
}

export interface AuditLogRow {
    id: number;
    rule_id: string;
    old_value: unknown;
    new_value: unknown;
    changed_by: string | null;
    changed_at: string;
    reason: string | null;
}

function isTransientRuleOverridesFetchError(message: string): boolean {
    return /failed to fetch|load failed|networkerror/i.test(message);
}

// ─── Validation ─────────────────────────────────────────────
//
// Admin can post arbitrary JSON into rule_overrides.value via Supabase.
// Validate per row against the rule's declared valueType so corrupt or
// stale entries can't propagate into domain calculations.

// Hand-written predicates rather than zod schemas. This module is reached
// from `RulesProvider`, which sits in the app entry chunk — importing zod
// for five typeof checks dragged ~230 kB of raw schema machinery into the
// landing page's critical path. The predicates below reproduce the zod v4
// semantics they replaced EXACTLY (verified against zod 4.4.3 directly, and
// pinned by `rulesRepo.valueTypes.test.ts`):
//   - `z.number()` rejects NaN and ±Infinity, so this is a FINITE check.
//   - `z.object({ id, value: z.unknown() })` requires the `value` KEY to be
//     present — `{ id }` alone is malformed, `{ id, value: undefined }` is not.
//   - arrays and `null` are not objects.

/** Matches `z.number()`: a finite number. NaN and ±Infinity are rejected. */
function isFiniteNumber(value: unknown): value is number {
    return typeof value === 'number' && Number.isFinite(value);
}

const VALUE_TYPE_PREDICATES: Record<RuleValueType, (value: unknown) => boolean> = {
    number: isFiniteNumber,
    string: (value) => typeof value === 'string',
    boolean: (value) => typeof value === 'boolean',
    // Index loop, NOT `.every()`: `Array.prototype.every` skips holes, so a
    // sparse `new Array(3)` would pass while `z.array(z.number())` rejects it
    // (it reads every index and sees `undefined`). A differential sweep against
    // zod 4.4.3 caught exactly this.
    'number[]': (value) => {
        if (!Array.isArray(value)) return false;
        for (let index = 0; index < value.length; index += 1) {
            if (!isFiniteNumber(value[index])) return false;
        }
        return true;
    },
    // `z.unknown()` accepts everything, `undefined` included.
    json: () => true,
};

function isValueValidForRule(ruleId: string, value: unknown): boolean {
    const def = RULES_BY_ID.get(ruleId);
    if (!def) return false;
    return VALUE_TYPE_PREDICATES[def.valueType](value);
}

/** Matches `z.object({ id: z.string(), value: z.unknown() })`. */
function parseRuleOverrideRow(
    raw: unknown,
): { id: string; value: unknown } | null {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
        return null;
    }
    const candidate = raw as Record<string, unknown>;
    if (typeof candidate.id !== 'string') return null;
    // The key must EXIST — `z.unknown()` accepts an explicit `undefined`
    // value but the object schema still requires the property.
    if (!('value' in candidate)) return null;
    return { id: candidate.id, value: candidate.value };
}

export function shouldSkipRuleOverridesFetch(
    currentHostname: string,
    configuredSupabaseUrl: string | undefined,
): boolean {
    if (!configuredSupabaseUrl) {
        return false;
    }

    const isLocalHostname = /^(localhost|127\.0\.0\.1)$/i.test(currentHostname);
    if (!isLocalHostname) {
        return false;
    }

    try {
        const apiHostname = new URL(configuredSupabaseUrl).hostname;
        return !/^(localhost|127\.0\.0\.1)$/i.test(apiHostname);
    } catch {
        return false;
    }
}

// ─── Fetch ──────────────────────────────────────────────────

/**
 * Load all active rule overrides from Supabase.
 * Returns a flat Record<ruleId, value> for merging with defaults.
 */
export async function fetchRuleOverrides(): Promise<RuleOverrides> {
    if (!supabase) {
        // Degraded (no backend): domain calculations use their defaults.
        return {};
    }
    if (typeof window !== 'undefined' && shouldSkipRuleOverridesFetch(window.location.hostname, supabaseUrl)) {
        return {};
    }

    const { data, error } = await supabase
        .from('rule_overrides')
        .select('id, value');

    if (error) {
        if (isTransientRuleOverridesFetchError(error.message)) {
            return {};
        }
        console.warn('Could not load rule overrides:', error.message);
        return {};
    }

    const overrides: RuleOverrides = {};
    for (const raw of data ?? []) {
        const row = parseRuleOverrideRow(raw);
        if (!row) {
            console.warn('Skipping malformed rule override row:', raw);
            continue;
        }
        if (!RULES_BY_ID.has(row.id)) {
            console.warn('Skipping rule override for unknown rule id:', row.id);
            continue;
        }
        if (!isValueValidForRule(row.id, row.value)) {
            console.warn(
                'Skipping rule override with value that does not match declared type:',
                row.id,
            );
            continue;
        }
        overrides[row.id] = row.value;
    }
    return overrides;
}

/**
 * Save (upsert) a rule override and log the change.
 */
export async function saveRuleOverride(
    ruleId: string,
    newValue: unknown,
    oldValue: unknown,
    reason?: string,
): Promise<{ success: boolean; error?: string }> {
    if (!supabase) return { success: false, error: 'backend-unavailable' };
    const { data: { user } } = await supabase.auth.getUser();
    const userId = user?.id ?? null;

    // Upsert the override
    const { error: upsertError } = await supabase
        .from('rule_overrides')
        .upsert(
            { id: ruleId, value: newValue, updated_by: userId },
            { onConflict: 'id' },
        );

    if (upsertError) {
        return { success: false, error: upsertError.message };
    }

    // Write audit log
    const { error: logError } = await supabase
        .from('rule_audit_log')
        .insert({
            rule_id: ruleId,
            old_value: oldValue,
            new_value: newValue,
            changed_by: userId,
            reason: reason ?? null,
        });

    if (logError) {
        console.warn('Audit log write failed:', logError.message);
    }

    return { success: true };
}

/**
 * Delete a rule override (revert to default).
 */
export async function deleteRuleOverride(
    ruleId: string,
    currentValue: unknown,
): Promise<{ success: boolean; error?: string }> {
    if (!supabase) return { success: false, error: 'backend-unavailable' };
    const { data: { user } } = await supabase.auth.getUser();
    const userId = user?.id ?? null;

    const { error } = await supabase
        .from('rule_overrides')
        .delete()
        .eq('id', ruleId);

    if (error) {
        return { success: false, error: error.message };
    }

    // Log the revert
    await supabase
        .from('rule_audit_log')
        .insert({
            rule_id: ruleId,
            old_value: currentValue,
            new_value: null,
            changed_by: userId,
            reason: 'Återställd till standardvärde',
        });

    return { success: true };
}

/**
 * Fetch recent audit log entries.
 */
export async function fetchAuditLog(
    limit = 50,
): Promise<AuditLogRow[]> {
    if (!supabase) return [];
    const { data, error } = await supabase
        .from('rule_audit_log')
        .select('*')
        .order('changed_at', { ascending: false })
        .limit(limit);

    if (error) {
        console.warn('Could not load audit log:', error.message);
        return [];
    }

    return data ?? [];
}
